// Custom hook encapsulating all data-fetching, mutations, and form state for the
// project detail page. Keeps the page component focused on layout and event wiring.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ProjectWithProgress, ProjectMember } from '@task-tracker/shared';
import { apiClient } from '../lib/api-client';
import { usePermission } from './use-permission';
import { useAuthStore } from '../stores/auth.store';
import { useProjectNonMembers, useProjectUsers } from './use-project-users';
import { projectRecentUpdatesKeys } from './use-project-recent-updates';
import { dateToIso, isoToDateInput } from '../lib/project-display';

type ProjectMutationResponse = { project: ProjectWithProgress };

/** Fields a task mutation can freshly report back about the project it touched. */
export type ProjectProgressPatch = Partial<
  Pick<ProjectWithProgress, 'progress_pct' | 'status' | 'task_count' | 'completed_task_count'>
>;

/** Merge freshly-computed progress into the cached project detail, for an
 *  instant repaint of the progress bar and the status badge.
 *
 *  A merge, deliberately, not a replace: `['project', id]` is shared with
 *  `use-project-type.ts` and `components/forms/FormView.tsx`, which read `type`,
 *  `name` and friends off the same entry. Writing a 4-field object there would
 *  blank those out. No-ops when nothing is cached yet — the invalidation that
 *  runs alongside will fetch it. */
export function patchProjectProgressCache(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
  patch: ProjectProgressPatch,
) {
  if (!projectId) return;
  queryClient.setQueryData<{ data: { project: ProjectWithProgress } }>(
    ['project', projectId],
    (old) => {
      if (!old?.data?.project) return old;
      return { ...old, data: { ...old.data, project: { ...old.data.project, ...patch } } };
    },
  );
}

export interface EditFormValues {
  name: string;
  work_order_number: string;
  description: string;
  location: string;
  status: string;
  start_date: string;
  target_end_date: string;
}

export function useProjectDetail(id: string | undefined) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const canEdit = usePermission('edit_projects');
  const canArchiveDelete = usePermission('archive_delete_projects');
  const currentUserId = useAuthStore((s) => s.user?.id);

  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditFormValues>({
    name: '',
    work_order_number: '',
    description: '',
    location: '',
    status: '',
    start_date: '',
    target_end_date: '',
  });
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  // ── Queries ─────────────────────────────────────────────────────────────────

  const { data: projectData, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => apiClient<{ project: ProjectWithProgress }>(`/api/v1/projects/${id}`),
    enabled: !!id,
  });

  const { data: membersData, isSuccess: membersLoaded } = useQuery({
    queryKey: ['project-members', id],
    queryFn: () => apiClient<{ members: ProjectMember[] }>(`/api/v1/projects/${id}/members`),
    enabled: !!id,
  });

  // Fetch all project users via the shared hook so every consumer of
  // ['project-users', id] sees the same normalized User[] cache shape.
  const { data: allUsers = [] } = useProjectUsers(id);

  const { data: nonMembers = [] } = useProjectNonMembers(id, canEdit);

  const project = projectData?.data?.project;
  const members = membersData?.data?.members ?? [];

  // Project chat is members-only (the API refuses everyone else, admins
  // included), so the Messages tab is hidden for a non-member.
  //
  // `membershipResolved` is not decoration: while this query is in flight
  // `members` is [], so a bare boolean would read "not a member" for a moment
  // and the tab would visibly pop in on every project open. Callers wait for
  // the answer instead of rendering the wrong one first.
  const membershipResolved = !id || membersLoaded;
  const isProjectMember = !!currentUserId && members.some((m) => m.user_id === currentUserId);

  function setProjectDetailCache(updatedProject: ProjectWithProgress) {
    if (!id) return;
    queryClient.setQueryData(['project', id], { data: { project: updatedProject } });
  }

  function invalidateRecentUpdates() {
    if (!id) return;
    void queryClient.invalidateQueries({ queryKey: projectRecentUpdatesKeys.list(id) });
  }

  // ── Mutations ────────────────────────────────────────────────────────────────

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient<ProjectMutationResponse>(`/api/v1/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (response) => {
      setProjectDetailCache(response.data.project);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      invalidateRecentUpdates();
      setEditing(false);
      toast.success('Project updated');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () =>
      apiClient<ProjectMutationResponse>(`/api/v1/projects/${id}/archive`, { method: 'POST' }),
    onSuccess: (response) => {
      setProjectDetailCache(response.data.project);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      invalidateRecentUpdates();
      toast.success('Project archived');
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () =>
      apiClient<ProjectMutationResponse>(`/api/v1/projects/${id}/unarchive`, { method: 'POST' }),
    onSuccess: (response) => {
      setProjectDetailCache(response.data.project);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      invalidateRecentUpdates();
      toast.success('Project unarchived');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient(`/api/v1/projects/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      invalidateRecentUpdates();
      toast.success('Project deleted');
      navigate('/projects');
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      apiClient(`/api/v1/projects/${id}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-members', id] });
      queryClient.invalidateQueries({ queryKey: ['project-users', id] });
      queryClient.invalidateQueries({ queryKey: ['project-non-members', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      invalidateRecentUpdates();
      setAddMemberUserId('');
      setAddMemberError(null);
      toast.success('Member added');
    },
    onError: (err) => {
      setAddMemberError(err instanceof Error ? err.message : 'Failed to add member.');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      apiClient(`/api/v1/projects/${id}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-members', id] });
      queryClient.invalidateQueries({ queryKey: ['project-users', id] });
      queryClient.invalidateQueries({ queryKey: ['project-non-members', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      invalidateRecentUpdates();
      toast.success('Member removed');
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function startEditing() {
    if (!project) return;
    setEditValues({
      name: project.name,
      work_order_number: project.work_order_number ?? '',
      description: project.description ?? '',
      location: project.location ?? '',
      status: project.status,
      start_date: isoToDateInput(project.start_date),
      target_end_date: isoToDateInput(project.target_end_date),
    });
    setEditing(true);
  }

  function handleEditChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    setEditValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleUpdateSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!project) return;
    setDateError(null);
    if (
      editValues.start_date &&
      editValues.target_end_date &&
      editValues.target_end_date < editValues.start_date
    ) {
      setDateError('Target end date must be on or after the start date.');
      return;
    }
    const body: Record<string, unknown> = {
      version: project.version,
      name: editValues.name,
      work_order_number: editValues.work_order_number.trim() || null,
      description: editValues.description || null,
      location: editValues.location || null,
      status: editValues.status,
      start_date: dateToIso(editValues.start_date),
      target_end_date: dateToIso(editValues.target_end_date),
    };
    updateMutation.mutate(body);
  }

  function handleAddMember() {
    if (!addMemberUserId) return;
    setAddMemberError(null);
    addMemberMutation.mutate(addMemberUserId);
  }

  return {
    project,
    members,
    allUsers,
    nonMembers,
    isLoading,
    isProjectMember,
    membershipResolved,
    canEdit,
    canArchiveDelete,
    editing,
    setEditing,
    editValues,
    addMemberUserId,
    setAddMemberUserId,
    addMemberError,
    dateError,
    updateMutation,
    archiveMutation,
    unarchiveMutation,
    deleteMutation,
    addMemberMutation,
    removeMemberMutation,
    startEditing,
    handleEditChange,
    handleUpdateSubmit,
    handleAddMember,
  };
}
